import { limparMonitor, oabsDe, saveHits, touchProfile } from '../../store/monitor';
import { getDoc } from '../../store/store';
import type { Profile } from '../../store/types';
import { consultar, consultarDiarios, RateLimited, TooBroad, type Pub } from './djen';
import { tribunalAceito } from './tribunais';
import { t } from '../../lib/i18n';

const wait = (ms: number) => new Promise(r => setTimeout(r, ms));
/** Dias de folga antes da última busca (pega publicações que o DJEN mostra com atraso). */
const FOLGA_DIAS = 7;

/** O filtro de OAB do DJEN compara texto exato: consulta com e sem zeros à esquerda. */
function variantesOAB(oab: string) {
  const d = oab.replace(/\D/g, '');
  return [...new Set([d.replace(/^0+/, ''), d.padStart(6, '0')])].filter(Boolean);
}

type Q = { name?: string; oab?: string; uf?: string; tribunal?: string };

/** Consultas ao DJEN de um perfil: nome e cada OAB; com até 3 tribunais escolhidos (e porTribunal), uma por tribunal. */
export function consultasDe(p: Profile, porTribunal = true): Q[] {
  const base: Q[] = [];
  if (p.name.trim()) base.push({ name: p.name.trim() });
  for (const o of oabsDe(p)) for (const n of variantesOAB(o.numero)) base.push({ oab: n, uf: o.uf });
  const trib = p.tribunais ?? [];
  if (!porTribunal || !trib.length || trib.length > 3) return base;
  return base.flatMap(q => trib.map(tr => ({ ...q, tribunal: tr })));
}

/** Roda as consultas; se o DJEN recusar o filtro de tribunal, refaz sem ele (o filtro local continua valendo). */
async function consultarPerfil(p: Profile, from: Date, to: Date, guardar: (pubs: Pub[]) => void) {
  /** Uma consulta; se vier grande demais para o período, consulta dia a dia (0.55: nada fica de fora). */
  const uma = async (q: Q) => {
    try { guardar(await consultar(q, from, to)); }
    catch (e) {
      if (!(e instanceof TooBroad)) throw e;
      for (let d = new Date(from); d <= to; d = new Date(d.getTime() + 864e5)) {
        guardar(await consultar(q, d, d, true)); await wait(1200);
      }
    }
  };
  const rodar = async (qs: Q[]) => { for (const q of qs) { await uma(q); await wait(1200); } };
  try { await rodar(consultasDe(p)); }
  catch (e) {
    if (e instanceof RateLimited || !consultasDe(p).some(q => q.tribunal)) throw e;
    await rodar(consultasDe(p, false));
  }
}

let rodando = false;

export async function verificar(): Promise<{ novos: number; erro?: string }> {
  if (rodando) return { novos: 0 };
  rodando = true;
  limparMonitor();
  let novos = 0, erro: string | undefined;
  try {
    for (const p of getDoc('monitor').profiles) {
      // 0.55: volta 7 dias antes da última busca (o DJEN às vezes mostra a publicação dias depois da data dela);
      // as repetidas são ignoradas, então nada entra duas vezes e nada fica de fora
      const ult = p.last_check ? new Date(p.last_check).getTime() : Date.now();
      const from = new Date(Math.max(ult - FOLGA_DIAS * 864e5, Date.now() - 30 * 864e5));
      const to = new Date();
      const filtrar = (pubs: Pub[]) => pubs.filter(x => tribunalAceito(x.court, p.tribunais));
      try {
        await consultarPerfil(p, from, to, pubs => { novos += saveHits(p.id, filtrar(pubs)); });
        if (p.diarios && p.name.trim()) { novos += saveHits(p.id, await consultarDiarios(p.name, from, to)); await wait(1200); }
        touchProfile(p.id);
      } catch (e) {
        if (e instanceof RateLimited) return { novos, erro: t('Limite de consultas atingido. Tente de novo em 1 minuto.') };
        erro = e instanceof TooBroad
          ? t('A busca de "{nome}" está ampla demais. Use o nome completo.', { nome: p.label })
          : t('Não foi possível consultar o DJEN agora.');
      }
    }
    return { novos, erro };
  } finally { rodando = false; }
}
